/**
 * ComposerSheet — the message composer modal, extracted from InboxScreen so
 * any screen can mount it.
 *
 * The screen contract is: pass `visible`, `projectId`, optional `inboxEmail`
 * + `initial`, and `onClose` / `onSent` callbacks. For the global pattern
 * (open from anywhere), use the `composer` redux slice + GlobalComposerHost
 * instead of mounting this component directly.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Modal,
  Pressable,
  KeyboardAvoidingView,
  Platform,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppDispatch } from '../store';
import { useSheetLayout } from '../utilities/sheetLayout';
import Icon from './Icon';
import { sendMessage } from '../store/actions/messages';
import { sendOrOfferPdf } from '../utilities/sendAsPdf';
import { pickFile, type PickedFile } from '../utilities/pickFile';
import { scanDocument, isScannerAvailable } from '../utilities/scanDocument';
import * as FileSystem from 'expo-file-system/legacy';
import { formatBytes } from '../utilities/formatFileSize';

export interface ComposerInitial {
  to?: string;
  subject?: string;
  body?: string;
}

interface Props {
  visible: boolean;
  initial: ComposerInitial;
  inboxEmail: string | null;
  projectId: number | string;
  onClose: () => void;
  onSent?: () => void;
  theme: any;
}

async function fileToBase64(uri: string): Promise<string> {
  return FileSystem.readAsStringAsync(uri, {
    encoding: (FileSystem.EncodingType as any).Base64 ?? 'base64',
  });
}

export default function ComposerSheet({
  visible,
  initial,
  inboxEmail,
  projectId,
  onClose,
  onSent,
  theme,
}: Props) {
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();
  const sheet = useSheetLayout({ desktopMaxWidth: 720, desktopMaxHeightRatio: 0.92 });
  const [to, setTo]               = useState('');
  const [subject, setSubject]     = useState('');
  const [body, setBody]           = useState('');
  const [sending, setSending]     = useState(false);
  const [attachments, setAttachments] = useState<PickedFile[]>([]);
  const [attaching, setAttaching] = useState(false);

  useEffect(() => {
    if (visible) {
      setTo(initial.to ?? '');
      setSubject(initial.subject ?? '');
      setBody(initial.body ?? '');
      setAttachments([]);
    }
  }, [visible, initial.to, initial.subject, initial.body]);

  const handleAddAttachment = useCallback(() => {
    const browseFiles = async () => {
      setAttaching(true);
      const files = await pickFile(true);
      setAttaching(false);
      if (files?.length) setAttachments((prev) => [...prev, ...files]);
    };

    if (Platform.OS === 'web' || !isScannerAvailable()) {
      browseFiles();
      return;
    }

    const options: Array<{ text: string; onPress: () => void }> = [
      { text: 'Browse / Google Drive / Dropbox', onPress: browseFiles },
      {
        text: 'Scan Document',
        onPress: async () => {
          setAttaching(true);
          try {
            const scanned = await scanDocument();
            if (scanned.length) setAttachments((prev) => [...prev, ...scanned]);
          } catch {
            // scanner cancelled or unavailable
          }
          setAttaching(false);
        },
      },
    ];

    Alert.alert(
      'Add Attachment',
      undefined,
      [
        ...options.map((o) => ({ text: o.text, onPress: o.onPress })),
        { text: 'Cancel', style: 'cancel' as const },
      ],
      { cancelable: true },
    );
  }, []);

  const handleRemoveAttachment = useCallback((idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleSend = useCallback(async () => {
    const trimTo = to.trim();
    if (!trimTo || sending) return;
    setSending(true);

    const builtAttachments = await Promise.all(
      attachments.map(async (f) => ({
        content: await fileToBase64(f.uri),
        type: f.mimeType,
        filename: f.name,
        size: f.size,
        isDocument: false,
      })),
    );

    const result = await sendOrOfferPdf((deliverAsPdf) => dispatch(sendMessage(
      {
        subject: subject.trim() || '(no subject)',
        body: body.trim(),
        project_id: projectId,
        attachments: builtAttachments,
      },
      [{ to_email: trimTo, to_cognito: null, to_first: null, to_last: null }],
      [],
      { deliverAsPdf },
    )));
    setSending(false);
    // Kept editing instead of sending the health details as a PDF.
    if (result === null) return;
    if ((result as any)?.success !== false) {
      onSent?.();
      onClose();
    } else {
      Alert.alert(
        'Message not sent',
        (result as any)?.message || 'Please try again in a moment.',
      );
    }
  }, [dispatch, to, subject, body, attachments, projectId, sending, onSent, onClose]);

  return (
    <Modal
      visible={visible}
      animationType={sheet.animationType}
      transparent={sheet.isDesktop}
      presentationStyle={sheet.isDesktop ? undefined : 'pageSheet'}
      onRequestClose={onClose}
    >
      <View style={[{ flex: 1 }, sheet.isDesktop && { backgroundColor: 'rgba(0,0,0,0.45)' }, sheet.overlayStyle]}>
        {sheet.isDesktop && <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />}
        <KeyboardAvoidingView
          style={[styles.composeWrap, { backgroundColor: theme.colors.background }, sheet.sheetStyle]}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <View style={[styles.composeHeader, { borderBottomColor: theme.colors.border }]}>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Text style={[styles.composeCancelText, { color: theme.colors.muted }]}>Cancel</Text>
            </TouchableOpacity>
            <Text style={[styles.composeTitle, { color: theme.colors.text }]}>New Message</Text>
            <TouchableOpacity
              onPress={handleSend}
              disabled={sending || !to.trim()}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              {sending
                ? <ActivityIndicator size="small" color={theme.colors.primary} />
                : <Text style={[styles.composeSendText, { color: to.trim() ? theme.colors.primary : theme.colors.muted }]}>Send</Text>
              }
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.composeBody} keyboardShouldPersistTaps="handled">
            {inboxEmail && (
              <View style={[styles.composeField, { borderBottomColor: theme.colors.border }]}>
                <Text style={[styles.composeLabel, { color: theme.colors.muted }]}>From</Text>
                <Text style={[styles.composeFromValue, { color: theme.colors.muted }]}>{inboxEmail}</Text>
              </View>
            )}
            <View style={[styles.composeField, { borderBottomColor: theme.colors.border }]}>
              <Text style={[styles.composeLabel, { color: theme.colors.muted }]}>To</Text>
              <TextInput
                style={[styles.composeInput, { color: theme.colors.text }]}
                value={to}
                onChangeText={setTo}
                placeholder="recipient@example.com"
                placeholderTextColor={theme.colors.muted}
                autoCapitalize="none"
                keyboardType="email-address"
                autoCorrect={false}
              />
            </View>
            <View style={[styles.composeField, { borderBottomColor: theme.colors.border }]}>
              <Text style={[styles.composeLabel, { color: theme.colors.muted }]}>Subject</Text>
              <TextInput
                style={[styles.composeInput, { color: theme.colors.text }]}
                value={subject}
                onChangeText={setSubject}
                placeholder="Subject"
                placeholderTextColor={theme.colors.muted}
              />
            </View>
            <TextInput
              style={[styles.composeBodyInput, { color: theme.colors.text }]}
              value={body}
              onChangeText={setBody}
              placeholder="Write your message…"
              placeholderTextColor={theme.colors.muted}
              multiline
              textAlignVertical="top"
            />
          </ScrollView>

          <View style={[styles.composeToolbar, { borderTopColor: theme.colors.border, paddingBottom: insets.bottom + 8 }]}>
            {attachments.length > 0 && (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                style={styles.attachmentRow}
                contentContainerStyle={styles.attachmentRowContent}
              >
                {attachments.map((f, idx) => (
                  <View
                    key={`${f.name}-${idx}`}
                    style={[styles.attachmentChip, { backgroundColor: theme.colors.card, borderColor: theme.colors.border }]}
                  >
                    <Icon icon={['fas', 'paperclip']} size={11} color={theme.colors.muted} />
                    <Text style={[styles.attachmentChipName, { color: theme.colors.text }]} numberOfLines={1}>
                      {f.name}
                    </Text>
                    <Text style={[styles.attachmentChipSize, { color: theme.colors.muted }]}>
                      {formatBytes(f.size)}
                    </Text>
                    <TouchableOpacity
                      onPress={() => handleRemoveAttachment(idx)}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                      <Icon icon={['fas', 'xmark']} size={11} color={theme.colors.muted} />
                    </TouchableOpacity>
                  </View>
                ))}
              </ScrollView>
            )}

            <TouchableOpacity
              style={styles.attachButton}
              onPress={handleAddAttachment}
              disabled={attaching}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              {attaching
                ? <ActivityIndicator size="small" color={theme.colors.muted} />
                : <Icon icon={['fas', 'paperclip']} size={15} color={theme.colors.muted} />
              }
            </TouchableOpacity>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  composeWrap: { flex: 1 },
  composeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 20,
    paddingBottom: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  composeTitle: { fontSize: 16, fontWeight: '700' },
  composeCancelText: { fontSize: 15 },
  composeSendText: { fontSize: 15, fontWeight: '600' },
  composeBody: { flex: 1, paddingHorizontal: 16 },
  composeField: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: 8,
  },
  composeLabel: { fontSize: 13, fontWeight: '600', width: 52 },
  composeFromValue: { fontSize: 13, flex: 1 },
  composeInput: { flex: 1, fontSize: 15, paddingVertical: 0 },
  composeBodyInput: { fontSize: 15, marginTop: 12, minHeight: 200, lineHeight: 22 },
  composeToolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
  },
  attachmentRow: { flex: 1 },
  attachmentRowContent: { gap: 6, alignItems: 'center' },
  attachmentChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
  },
  attachmentChipName: { maxWidth: 120, fontSize: 12 },
  attachmentChipSize: { fontSize: 11 },
  attachButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
